import { Download, FileWarning } from 'lucide-react';
import { useState } from 'react';
import { downloadDocument, useDocumentObjectUrl } from '../../apis/documents-http';
import { cn } from '../../lib/cn';
import { useI18nTranslation } from '../../lib/use-i18n-translation';
import { Button } from './Button';
import { DocumentImage } from './DocumentImage';
import { Spinner } from './Spinner';

export interface DocumentFrameProps {
  documentId: string;
  mime: string;
  /** The name the "Open" button saves the file under. */
  fileName: string;
  title: string;
  className?: string;
}

/**
 * A document preview: an image for a photograph, an embedded viewer for a PDF (plan §2.5, §2.6).
 *
 * Both render from an object URL, so a PDF is shown by the browser's own viewer without the file ever
 * being reachable by a URL that carries a token. The "Open" button downloads through an object-URL
 * anchor for the same reason: `<a href="/api/…" download>` is an unauthenticated navigation and would
 * save a 401 error page under the document's name.
 *
 * The fallback matters more than it looks. A `<object>` that cannot render a PDF shows its children
 * instead of failing silently, so a browser with its PDF viewer disabled — which is the default in some
 * managed environments — still offers the person a way to see the file.
 */
export const DocumentFrame = ({ documentId, mime, fileName, title, className }: DocumentFrameProps) => {
  const { t } = useI18nTranslation();
  const isImage = mime.startsWith('image/');
  const { url, isLoading, error } = useDocumentObjectUrl(isImage ? null : documentId);
  const [isSaving, setSaving] = useState(false);

  const save = async (): Promise<void> => {
    setSaving(true);

    try {
      await downloadDocument(documentId, fileName);
    } finally {
      setSaving(false);
    }
  };

  if (isImage) {
    return (
      <div className={cn('space-y-2', className)}>
        <DocumentImage
          documentId={documentId}
          alt={title}
          frameClassName="aspect-[4/3]"
        />
        <SaveButton
          isSaving={isSaving}
          onSave={() => void save()}
          label={t('documents.open')}
        />
      </div>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className="h-96 w-full overflow-hidden rounded-md border border-border bg-muted">
        {isLoading ? (
          <div className="flex size-full items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner />
            {t('app.loading')}
          </div>
        ) : null}

        {error !== null ? (
          <div className="flex size-full flex-col items-center justify-center gap-2 p-4 text-center">
            <FileWarning className="size-5 text-muted-foreground" />
            <p className="text-xs text-destructive">{t('documents.previewFailed')}</p>
          </div>
        ) : null}

        {url !== null ? (
          <object
            data={url}
            type={mime}
            title={title}
            className="size-full"
          >
            {/* Shown when the browser has no PDF viewer: the file is still one click away. */}
            <div className="flex size-full flex-col items-center justify-center gap-2 p-4 text-center">
              <p className="text-sm text-muted-foreground">{t('documents.noInlineViewer')}</p>
              <SaveButton
                isSaving={isSaving}
                onSave={() => void save()}
                label={t('documents.open')}
              />
            </div>
          </object>
        ) : null}
      </div>

      <SaveButton
        isSaving={isSaving}
        onSave={() => void save()}
        label={t('documents.open')}
      />
    </div>
  );
};

const SaveButton = ({ isSaving, onSave, label }: { isSaving: boolean; onSave: () => void; label: string }) => (
  <Button
    variant="outline"
    size="sm"
    isLoading={isSaving}
    onClick={onSave}
  >
    <Download className="size-3.5" />
    {label}
  </Button>
);
